import React, { useState } from 'react';
import { motion } from 'framer-motion';
import './Tokenomics.css';

interface TokenomicsData {
  label: string;
  value: number;
  color: string;
  description: string;
}

const Tokenomics: React.FC = () => {
  const [activeSegment, setActiveSegment] = useState<number | null>(null);

  const distributionData: TokenomicsData[] = [
    {
      label: 'Reserve Fund',
      value: 45,
      color: '#3b82f6',
      description: 'USD reserves backing the stablecoin 1:1'
    },
    {
      label: 'Treasury',
      value: 25,
      color: '#60a5fa',
      description: 'Protocol treasury for operations and development'
    },
    {
      label: 'Liquidity Pool',
      value: 20,
      color: '#93c5fd',
      description: 'DEX liquidity and market making'
    },
    {
      label: 'Ecosystem Fund',
      value: 10,
      color: '#dbeafe',
      description: 'Grants and ecosystem development'
    }
  ];

  const supplyMechanics = {
    minting: [
      'Direct minting with approved stablecoins (USDC, USDT, DAI)',
      'Fiat onboarding through licensed partners',
      'Minimum minting thresholds for institutional users'
    ],
    redemption: [
      '1:1 redemption guarantee for qualified holders',
      'T+2 settlement timeline for standard redemptions',
      'Expedited redemption options available'
    ],
    fees: [
      '0.25% minting fee (waived for institutional partners)',
      '0.15% redemption fee',
      '0.35% APY management fee for reserve maintenance'
    ]
  };

  const distributionAllocation = {
    initial: [
      'Private sale to strategic partners',
      'Public offering through licensed exchanges',
      'Ecosystem development fund allocation'
    ],
    utility: [
      'Medium of exchange for all type of services',
      'Collateral for DeFi applications',
      'Settlement asset for institutional transactions'
    ]
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.6,
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6 }
    }
  };

  const calculateStrokeDasharray = (value: number, radius: number) => {
    const circumference = 2 * Math.PI * radius;
    const fillPercentage = value / 100;
    return `${fillPercentage * circumference} ${circumference}`;
  };

  const calculateStrokeDashoffset = (startAngle: number, radius: number) => {
    const circumference = 2 * Math.PI * radius;
    return circumference - (startAngle / 360) * circumference;
  };

  return (
    <section id="tokenomics" className="tokenomics section-padding">
      <div className="container">
        <motion.div
          className="tokenomics-header"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
        
          <h2 className="section-title">
            Transparent Economic<br />
            <span className="title-gradient">Model & Distribution</span>
          </h2>
          <p className="section-subtitle">
            Built on a foundation of transparency and sustainability, USD.P employs 
            a robust economic model designed for long-term stability and growth.
          </p>
        </motion.div>

        <div className="tokenomics-content">
          {/* Distribution Chart */}
          <motion.div
            className="distribution-section"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            <motion.div className="chart-container" variants={itemVariants}>
              <h3 className="chart-title">Bridged E-Pay Tether Distribution</h3>
              <div className="chart-wrapper">
                <svg viewBox="0 0 200 200" className="distribution-chart">
                  <circle
                    cx="100"
                    cy="100"
                    r="80"
                    fill="transparent"
                    stroke="rgba(255, 255, 255, 0.1)"
                    strokeWidth="4"
                  />
                  {distributionData.map((segment, index) => {
                    const startAngle = distributionData
                      .slice(0, index)
                      .reduce((sum, item) => sum + (item.value / 100) * 360, 0);

                    return (
                      <motion.circle
                        key={segment.label}
                        cx="100"
                        cy="100"
                        r="80"
                        fill="transparent"
                        stroke={segment.color}
                        strokeWidth="8"
                        strokeDasharray={calculateStrokeDasharray(segment.value, 80)}
                        strokeDashoffset={calculateStrokeDashoffset(startAngle, 80)}
                        className={`chart-segment ${activeSegment === index ? 'active' : ''}`}
                        onMouseEnter={() => setActiveSegment(index)}
                        onMouseLeave={() => setActiveSegment(null)}
                        initial={{ strokeDasharray: '0 502' }}
                        animate={{ strokeDasharray: calculateStrokeDasharray(segment.value, 80) }}
                        transition={{ duration: 1, delay: index * 0.2 }}
                        whileHover={{ strokeWidth: 12 }}
                      />
                    );
                  })}
                  <image
                    x="60"
                    y="60"
                    width="80"
                    height="80"
                    href="/USDPlogo.png"
                    className="chart-center-image"
                  />
                </svg>
              </div>
            </motion.div>

            <motion.div className="distribution-legend" variants={itemVariants}>
              {distributionData.map((item, index) => (
                <motion.div
                  key={item.label}
                  className={`legend-item ${activeSegment === index ? 'active' : ''}`}
                  onMouseEnter={() => setActiveSegment(index)}
                  onMouseLeave={() => setActiveSegment(null)}
                  whileHover={{ scale: 1.02 }}
                >
                  <div className="legend-color" style={{ backgroundColor: item.color }}></div>
                  <div className="legend-content">
                    <div className="legend-label">{item.label}</div>
                    <div className="legend-value">{item.value}%</div>
                    <div className="legend-description">{item.description}</div>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>

          {/* Supply Mechanics Section */}
          <motion.div
            className="supply-mechanics-section"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            <motion.h3 className="subsection-title" variants={itemVariants}>
              Supply Mechanics
            </motion.h3>
            
            <div className="mechanics-grid">
              <motion.div className="mechanics-card" variants={itemVariants}>
                <h4 className="mechanics-title">Minting Process</h4>
                <ul className="mechanics-list">
                  {supplyMechanics.minting.map((item, index) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>
              </motion.div>

              <motion.div className="mechanics-card" variants={itemVariants}>
                <h4 className="mechanics-title">Redemption Mechanism</h4>
                <ul className="mechanics-list">
                  {supplyMechanics.redemption.map((item, index) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>
              </motion.div>

              <motion.div className="mechanics-card" variants={itemVariants}>
                <h4 className="mechanics-title">Fee Structure</h4>
                <ul className="mechanics-list">
                  {supplyMechanics.fees.map((item, index) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>
              </motion.div>
            </div>
          </motion.div>

          {/* Distribution and Allocation Section */}
          <motion.div
            className="distribution-allocation-section"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            <motion.h3 className="subsection-title" variants={itemVariants}>
              Distribution and Allocation
            </motion.h3>
            
            <div className="allocation-grid">
              <motion.div className="allocation-card" variants={itemVariants}>
                <h4 className="allocation-title">Initial Distribution</h4>
                <ul className="allocation-list">
                  {distributionAllocation.initial.map((item, index) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>
              </motion.div>

              <motion.div className="allocation-card" variants={itemVariants}>
                <h4 className="allocation-title">Token Utility</h4>
                <ul className="allocation-list">
                  {distributionAllocation.utility.map((item, index) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>

      <div className="tokenomics-background">
        <div className="bg-grid"></div>
        <div className="bg-glow glow-left"></div>
        <div className="bg-glow glow-right"></div>
      </div>
    </section>
  );
};

export default Tokenomics;